/**
 * Editorial experience — hero, closing frame, collage scroll choreography (respects Reduced Motion).
 * Brush layers live inside background layers and scale with them (no separate parallax).
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function smoothstep(t) {
    t = clamp(t, 0, 1);
    return t * t * (3 - 2 * t);
  }

  function syncIntroToTitle(root) {
    var hero = root.querySelector('header.ee-hero') || root.querySelector('[data-ee-hero]');
    if (!hero) return;
    var title = hero.querySelector('.ee-title');
    var intro = hero.querySelector('.ee-hero-intro');
    if (!title || !intro) return;
    intro.style.setProperty('color', getComputedStyle(title).color, 'important');
  }

  function bindScrollCue(root) {
    root.querySelectorAll('[data-ee-scroll-target]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sel = btn.getAttribute('data-ee-scroll-target');
        if (!sel) return;
        var el = document.querySelector(sel);
        if (el) el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  function tryPlayEditorialVideo(v) {
    if (!v || v.tagName !== 'VIDEO') return;
    v.muted = true;
    v.setAttribute('muted', '');
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {});
    }
  }

  function markVideoReady(root) {
    root.querySelectorAll('.ee-media__video').forEach(function (v) {
      tryPlayEditorialVideo(v);
      v.addEventListener('loadeddata', function () {
        var s = v.closest('.ee-media-stack');
        if (s) s.classList.add('ee-video--ready');
        tryPlayEditorialVideo(v);
      });
      v.addEventListener('canplay', function () {
        tryPlayEditorialVideo(v);
      });
      if (v.readyState >= 2) {
        var s = v.closest('.ee-media-stack');
        if (s) s.classList.add('ee-video--ready');
      }
    });

    if (typeof IntersectionObserver !== 'undefined') {
      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) tryPlayEditorialVideo(en.target);
          });
        },
        { root: null, rootMargin: '0px 0px 12% 0px', threshold: 0.01 }
      );
      root.querySelectorAll('.ee-media__video').forEach(function (v) {
        obs.observe(v);
      });
    }
  }

  function updateHero(root) {
    var hero = root.querySelector('[data-ee-hero]');
    var bgInner = hero && hero.querySelector('[data-ee-hero-bg-inner]');
    if (!hero || !bgInner) return;

    var zoomPct = parseFloat(root.getAttribute('data-ee-hero-zoom'), 10);
    if (isNaN(zoomPct)) zoomPct = 8;

    var h = hero.offsetHeight;
    if (h <= 0) return;

    var top = hero.getBoundingClientRect().top;
    var progress = clamp(-top / Math.max(h * 0.88, 1), 0, 1);
    var eased = smoothstep(progress);
    var scale = 1 + (zoomPct / 100) * eased;
    bgInner.style.transform = 'scale(' + scale + ')';

    var content = hero.querySelector('.ee-hero__content');
    if (content) {
      content.style.opacity = String(1 - 0.35 * eased);
    }
  }

  function updateClosingFrame(root) {
    var frame = root.querySelector('[data-ee-closing-frame]');
    var inner = frame && frame.querySelector('[data-ee-closing-frame-bg-inner]');
    if (!frame || !inner) return;

    var zoomPct = parseFloat(frame.getAttribute('data-ee-closing-zoom'), 10);
    if (isNaN(zoomPct)) zoomPct = 8;

    var h = frame.offsetHeight;
    if (h <= 0) return;

    var top = frame.getBoundingClientRect().top;
    var progress = clamp(-top / Math.max(h * 0.88, 1), 0, 1);
    var eased = smoothstep(progress);
    var scale = 1 + (zoomPct / 100) * eased;
    inner.style.transform = 'scale(' + scale + ')';
  }

  var brushStrokeLenCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

  function getPathLength(path) {
    if (brushStrokeLenCache && brushStrokeLenCache.has(path)) {
      return brushStrokeLenCache.get(path);
    }
    var len = 0;
    try {
      len = typeof path.getTotalLength === 'function' ? path.getTotalLength() : 0;
    } catch (e) {
      len = 0;
    }
    if (len > 0 && brushStrokeLenCache) brushStrokeLenCache.set(path, len);
    return len;
  }

  function brushScrollProgressForPath(root, path) {
    var scene = path.closest('[data-ee-scene]');
    if (scene) {
      var sticky = scene.querySelector('.ee-scene-sticky');
      var sh = sticky ? sticky.offsetHeight : window.innerHeight;
      var outerTop = scene.getBoundingClientRect().top + window.scrollY;
      var scrolled = window.scrollY - outerTop;
      var scrollRange = Math.max(scene.offsetHeight - sh, 1);
      return clamp(scrolled / scrollRange, 0, 1);
    }
    var rect = root.getBoundingClientRect();
    var vh = window.innerHeight;
    var span = Math.max(root.offsetHeight - vh * 0.5, vh);
    var top = rect.top + window.scrollY;
    var y = window.scrollY + vh * 0.35;
    return span > 0 ? clamp((y - top) / span, 0, 1.15) : 0;
  }

  function wingForBrushPath(path) {
    var w = path.getAttribute('data-ee-brush-wing');
    if (w) return w;
    var marked = path.closest('[data-ee-brush-wing]');
    if (marked) return marked.getAttribute('data-ee-brush-wing') || 'right';
    if (path.closest('.ee-brush-wing--left')) return 'left';
    if (path.closest('.ee-brush-on-fig, .ee-brush-top-strip')) return 'top';
    return 'right';
  }

  function updateBrushDraw(root) {
    root.querySelectorAll('[data-ee-brush-stroke]').forEach(function (path) {
      var order = parseInt(path.getAttribute('data-ee-stroke-order'), 10);
      if (isNaN(order)) order = 0;
      var scene = path.closest('[data-ee-scene]');
      var pat = scene ? parseInt(scene.getAttribute('data-ee-brush-pattern'), 10) || 0 : 0;
      pat = ((pat % 3) + 3) % 3;
      var wing = wingForBrushPath(path);
      var phase = 0;
      if (scene) {
        /* Collage scenes only: alternate which side starts (1=left lead, 2=right lead, 3=left, …). Top strokes still use pat. */
        var sceneIx = parseInt(scene.getAttribute('data-ee-brush-scene-ix'), 10);
        if (isNaN(sceneIx)) sceneIx = 1;
        var leadRight = sceneIx % 2 === 0;
        if (wing === 'left') {
          phase = leadRight ? 0.08 : -0.12;
        } else if (wing === 'right') {
          phase = leadRight ? -0.12 : 0.07;
        } else {
          if (pat === 0) phase = -0.06;
          else if (pat === 1) phase = 0.05;
          else phase = -0.03;
        }
      } else {
        if (pat === 0) {
          if (wing === 'left') phase = -0.12;
          else if (wing === 'right') phase = 0.07;
          else phase = -0.06;
        } else if (pat === 1) {
          if (wing === 'left') phase = 0.08;
          else if (wing === 'right') phase = -0.12;
          else phase = 0.05;
        } else {
          if (wing === 'left') phase = 0.04;
          else if (wing === 'right') phase = 0.04;
          else phase = -0.03;
        }
      }
      var stagger = order * 0.042 + phase;
      var gp = brushScrollProgressForPath(root, path);
      var local = smoothstep(clamp((gp - stagger) / 0.38, 0, 1));
      var len = getPathLength(path);
      if (len <= 0) return;
      path.style.strokeDasharray = String(len) + ' ' + String(len);
      path.style.strokeDashoffset = String(len * (1 - local));
    });
  }

  function clearBrushDraw(root) {
    root.querySelectorAll('[data-ee-brush-stroke]').forEach(function (path) {
      path.style.strokeDasharray = '';
      path.style.strokeDashoffset = '';
    });
  }

  function updateScene(outer) {
    var hasCenter = outer.hasAttribute('data-ee-has-center');
    var hasClosingCenter = outer.hasAttribute('data-ee-has-closing-center');
    var zoomPct = parseFloat(outer.getAttribute('data-zoom-percent'), 10);
    if (isNaN(zoomPct)) zoomPct = 0;
    var rightAfter = parseFloat(outer.getAttribute('data-right-reveal-after'), 10);
    if (isNaN(rightAfter)) rightAfter = 0;
    var rightBlend = parseFloat(outer.getAttribute('data-right-reveal-blend'), 10);
    if (isNaN(rightBlend) || rightBlend < 1) rightBlend = 320;
    var leftFadeStart = parseFloat(outer.getAttribute('data-left-fade-start'), 10);
    if (isNaN(leftFadeStart)) leftFadeStart = 0;

    var sticky = outer.querySelector('.ee-scene-sticky');
    var sh = sticky ? sticky.offsetHeight : window.innerHeight;
    var outerTop = outer.getBoundingClientRect().top + window.scrollY;
    var y = window.scrollY;
    var scrolled = y - outerTop;
    var scrollRange = outer.offsetHeight - sh;
    var p = scrollRange > 0 ? clamp(scrolled / scrollRange, 0, 1) : 0;

    var bgInner = outer.querySelector('[data-ee-scene-bg-inner]');
    if (bgInner) {
      var bgScale = 1 + (zoomPct / 100) * p;
      bgInner.style.transform = 'scale(' + bgScale + ')';
    }

    var centerWrap = outer.querySelector('[data-ee-center-wrap]');
    var centerFig = outer.querySelector('[data-ee-center-depth]');
    var closingWrap = outer.querySelector('[data-ee-center-closing-wrap]');
    var closingFig = outer.querySelector('[data-ee-center-closing-depth]');
    var leftWrap = outer.querySelector('[data-ee-left-wrap]');
    var rightWrap = outer.querySelector('[data-ee-right-wrap]');
    var leftDepth = outer.querySelector('[data-ee-left-depth]');
    var rightDepth = outer.querySelector('[data-ee-right-depth]');
    var handoff = outer.querySelector('[data-ee-scene-handoff]');

    if (hasCenter && centerWrap && centerFig) {
      var fade0 = 0.26;
      var fade1 = 0.56;
      var crossT = clamp((p - fade0) / Math.max(fade1 - fade0, 0.001), 0, 1);
      var cross = smoothstep(crossT);
      var centerOpacity = 1 - cross;
      centerWrap.style.opacity = String(centerOpacity);

      var scaleStart = 1.06;
      var scaleEnd = 0.88;
      var centerScale = scaleStart + (scaleEnd - scaleStart) * smoothstep(clamp(p / 0.62, 0, 1));
      centerFig.style.transform = 'scale(' + centerScale + ')';
    } else {
      if (centerWrap) centerWrap.style.opacity = '';
      if (centerFig) centerFig.style.transform = '';
    }

    if (hasClosingCenter && closingWrap && closingFig) {
      var cin0;
      var cin1;
      var cout0;
      var cout1;
      if (hasCenter) {
        cin0 = 0.42;
        cin1 = 0.62;
        cout0 = 0.78;
        cout1 = 0.96;
      } else {
        cin0 = 0.08;
        cin1 = 0.38;
        cout0 = 0.65;
        cout1 = 0.92;
      }
      var cInRaw = clamp((p - cin0) / Math.max(cin1 - cin0, 0.001), 0, 1);
      var cOutRaw = clamp((p - cout0) / Math.max(cout1 - cout0, 0.001), 0, 1);
      var cIn = smoothstep(smoothstep(cInRaw));
      var cOut = smoothstep(smoothstep(cOutRaw));
      var closOp = clamp(cIn * (1 - 0.98 * cOut), 0, 1);
      closingWrap.style.opacity = String(closOp);

      var cScaleIn = smoothstep(smoothstep(cInRaw));
      var cScaleOut = smoothstep(smoothstep(cOutRaw));
      var closScale = 0.82 + 0.22 * cScaleIn - 0.18 * cScaleOut;
      closingFig.style.transform = 'scale(' + clamp(closScale, 0.78, 1.04) + ')';
    } else {
      if (closingWrap) closingWrap.style.opacity = '';
      if (closingFig) closingFig.style.transform = '';
    }

    var scrollPx = clamp(scrolled, 0, Math.max(outer.offsetHeight, 0));
    var rOp = smoothstep(clamp((scrollPx - rightAfter) / rightBlend, 0, 1));

    var leftIn0 = 0.24;
    var leftIn1 = 0.52;
    var leftOp = hasCenter
      ? smoothstep(clamp((p - leftIn0) / Math.max(leftIn1 - leftIn0, 0.001), 0, 1))
      : 1;

    /* Left column exits first; right column exits after */
    var leftExit = smoothstep(clamp((p - 0.48) / 0.26, 0, 1));
    var rightExit = smoothstep(clamp((p - 0.76) / 0.2, 0, 1));

    var leftScale;
    if (hasCenter) {
      leftScale = (0.9 + 0.1 * leftOp) * (1 - 0.16 * leftExit);
      if (leftWrap) leftWrap.style.opacity = String(clamp(leftOp * (1 - 0.92 * leftExit), 0, 1));
    } else {
      var lMid = smoothstep(clamp(p / 0.34, 0, 1));
      leftScale = 0.92 + 0.08 * lMid - 0.14 * leftExit;
      if (leftWrap) leftWrap.style.opacity = String(clamp(1 - 0.92 * leftExit, 0, 1));
    }
    if (leftDepth) leftDepth.style.transform = 'scale(' + clamp(leftScale, 0.82, 1.08) + ')';

    var rightScale;
    if (hasCenter) {
      rightScale = (0.9 + 0.1 * rOp) * (1 - 0.16 * rightExit);
      if (rightWrap) rightWrap.style.opacity = String(clamp(rOp * (1 - 0.92 * rightExit), 0, 1));
    } else {
      rightScale = 0.92 + 0.08 * rOp - 0.14 * rightExit;
      if (rightWrap) rightWrap.style.opacity = String(clamp(rOp * (1 - 0.92 * rightExit), 0, 1));
    }
    if (rightDepth) rightDepth.style.transform = 'scale(' + clamp(rightScale, 0.82, 1.08) + ')';

    if (leftFadeStart > 0 && leftWrap) {
      var startP = leftFadeStart / 100;
      if (p > startP) {
        var lf = (p - startP) / Math.max(1 - startP, 0.001);
        var fadeL = 1 - smoothstep(clamp(lf, 0, 1));
        var cur = parseFloat(leftWrap.style.opacity, 10);
        if (isNaN(cur)) cur = 1;
        leftWrap.style.opacity = String(clamp(Math.min(cur, fadeL), 0, 1));
      }
    }

    if (handoff) {
      var ho = 0;
      if (hasCenter) {
        ho = smoothstep(clamp((p - 0.24) / 0.32, 0, 1)) * (1 - smoothstep(clamp((p - 0.52) / 0.22, 0, 1)));
      }
      handoff.style.opacity = String(ho * 0.9);
    }
  }

  function clearSceneTransforms(outer) {
    var bgInner = outer.querySelector('[data-ee-scene-bg-inner]');
    if (bgInner) bgInner.style.transform = '';
    var centerWrap = outer.querySelector('[data-ee-center-wrap]');
    var centerFig = outer.querySelector('[data-ee-center-depth]');
    if (centerWrap) centerWrap.style.opacity = '';
    if (centerFig) centerFig.style.transform = '';
    var closingWrap = outer.querySelector('[data-ee-center-closing-wrap]');
    var closingFig = outer.querySelector('[data-ee-center-closing-depth]');
    if (closingWrap) closingWrap.style.opacity = '';
    if (closingFig) closingFig.style.transform = '';
    var leftWrap = outer.querySelector('[data-ee-left-wrap]');
    var rightWrap = outer.querySelector('[data-ee-right-wrap]');
    var leftDepth = outer.querySelector('[data-ee-left-depth]');
    var rightDepth = outer.querySelector('[data-ee-right-depth]');
    if (leftWrap) leftWrap.style.opacity = '1';
    if (rightWrap) rightWrap.style.opacity = '1';
    if (leftDepth) leftDepth.style.transform = '';
    if (rightDepth) rightDepth.style.transform = '';
    var handoff = outer.querySelector('[data-ee-scene-handoff]');
    if (handoff) handoff.style.opacity = '';
  }

  function clearHeroTransforms(root) {
    var hero = root.querySelector('[data-ee-hero]');
    var bgInner = hero && hero.querySelector('[data-ee-hero-bg-inner]');
    if (bgInner) bgInner.style.transform = '';
    var content = hero && hero.querySelector('.ee-hero__content');
    if (content) content.style.opacity = '';
  }

  function clearClosingFrameTransforms(root) {
    var inner = root.querySelector('[data-ee-closing-frame-bg-inner]');
    if (inner) inner.style.transform = '';
  }

  function bindScrollChoreography(root) {
    var reduced = prefersReducedMotion();
    var scenes = root.querySelectorAll('[data-ee-scene]');
    var raf = 0;

    function tick() {
      raf = 0;
      if (prefersReducedMotion()) {
        clearHeroTransforms(root);
        clearClosingFrameTransforms(root);
        clearBrushDraw(root);
        scenes.forEach(clearSceneTransforms);
        return;
      }
      updateHero(root);
      updateClosingFrame(root);
      scenes.forEach(updateScene);
      updateBrushDraw(root);
    }

    function requestTick() {
      if (raf) return;
      raf = window.requestAnimationFrame(tick);
    }

    if (reduced) {
      clearHeroTransforms(root);
      clearClosingFrameTransforms(root);
      clearBrushDraw(root);
      scenes.forEach(clearSceneTransforms);
      return;
    }

    tick();
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
  }

  function init(root) {
    if (!root || root.getAttribute('data-ee-init') === 'true') return;
    root.setAttribute('data-ee-init', 'true');
    syncIntroToTitle(root);
    bindScrollCue(root);
    markVideoReady(root);
    bindScrollChoreography(root);
  }

  function boot() {
    document.querySelectorAll('.ee-root[data-editorial-experience]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    var r = ev.target && ev.target.querySelector && ev.target.querySelector('.ee-root[data-editorial-experience]');
    if (r) init(r);
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll('.ee-root[data-editorial-experience]').forEach(syncIntroToTitle);
  });
})();
